/**
 * calendar/event-detail.js — Opening the event detail modal, and the calendar's drag/resize write.
 *
 * The modal itself (details, reschedule, delete) is owned by
 * controllers/esm/event_detail_controller.js on #eventModal, which both schedule views
 * include. This module is the one way to open it, used by the calendar (core.js
 * eventClick) and the Dispatch board (dispatch_controller.js), plus what a change
 * means for the calendar: repaint from the server feed (the SSOT) so no detached
 * duplicate can linger.
 *
 * openEventDetail has no dependencies and no side effects, so both bundles can import it;
 * the calendar-only functions below reach FullCalendar through `state`.
 */

import { repaintCalendarEvents } from './feed.js';
import { EVENTS_API, state } from './state.js';

/**
 * Open the detail modal for one visit.
 *
 * @param event      the calendar feed's shape: { id, title, start: Date, end: Date|null,
 *                   allDay, extendedProps } (a FullCalendar EventApi satisfies it)
 * @param onChanged  called with { kind: 'rescheduled'|'assigned'|'deleted', id } after a write
 */
export function openEventDetail(event, onChanged) {
    const modal = document.getElementById('eventModal');
    if (!modal) throw new Error('openEventDetail: #eventModal is not on this page');
    modal.dispatchEvent(new CustomEvent('event-detail:open', { detail: { event, onChanged } }));
}

/**
 * A visit rendered into markup as `data-event='{{ visit.to_fullcalendar_dict() | tojson }}'`,
 * in the shape openEventDetail takes. The feed's dates are local ISO strings.
 */
export function visitFromData(json) {
    const data = JSON.parse(json);
    return { ...data, start: new Date(data.start), end: data.end ? new Date(data.end) : null };
}

// What a change from the modal means for the calendar. Exported for core's eventClick.
export function applyCalendarChange({ kind, id }) {
    if (kind === 'deleted') {
        // Remove all segments (multi-day events may be split across weekends)
        state.calendar.getEvents()
            .filter(e => e.id === String(id))
            .forEach(e => e.remove());
        return;
    }
    // Repaint from the feed: it returns the updated times and re-splits across weekends
    // via expandEvents. Clears-then-refetches so no detached duplicate lingers.
    repaintCalendarEvents();
}

// Persist times after a drag/resize. Exported for core's eventDrop/eventResize handlers.
export async function updateEventTimes(info) {
    const event = info.event;
    try {
        const response = await fetch(`${EVENTS_API}/${event.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                start_datetime: event.start.toISOString(),
                end_datetime: event.end ? event.end.toISOString() : null,
                all_day: event.allDay
            })
        });

        if (response.ok) {
            if (typeof window.showNotification === 'function') {
                window.showNotification('Event updated', 'success');
            }
        } else {
            const data = await response.json();
            const errMsg = data.error || 'Failed to update event';
            if (typeof window.showNotification === 'function') {
                window.showNotification(errMsg, 'error');
            } else {
                alert('Error: ' + errMsg);
            }
            info.revert();
        }
    } catch (error) {
        console.error('Error updating event:', error);
        if (typeof window.showNotification === 'function') {
            window.showNotification('Failed to update event', 'error');
        }
        info.revert();
    }
}
